import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Button from "../../../components/Button";
import {
  getSessionCollateralRequest,
  getSessionCurrentBlockedAmountDetailsRequest,
  getAllSessionsRequested,
} from "../../../redux/actions/intraSeanceCollaterals";
import DataTableServerSideRendering from "../../../components/DataTable/DataTableServerSideRendering";
import Select from "../../../components/Inputs/Select";

import { operationHistoryTrans } from "../../../constants/translations";

import moment from "moment";
import {
  convertNumberToFrontend,
  convertAmountToFrontend,
} from "../../../helpers/convert";

export default function IntraSeanceCollateral({ t }) {
  const dispatch = useDispatch();
  const allSessionList = useSelector(
    (state) => state.intraSeanceCollaterals.allSessionList
  );
  const { inSessionCollateralInformation, currentBlockedPriceDetailList } =
    useSelector((state) => state.intraSeanceCollaterals);

  const { organizationId, organizationName } = useSelector(
    (state) => state.authorization.user.organization
  );

  const transactionTypeTranslation = operationHistoryTrans(t);
  const [sessionDate, setSessionDate] = useState(null);

  const sessionCollateralStatusColumns = [
    {
      name: t("pages.default.IntraSeanceCollateral.table.transactionDate"),
      selector: "transactionDate",
      cell: (row) =>
        (moment(row?.transactionDate).isValid() &&
          moment(row?.transactionDate).format("DD/MM/YYYY HH:mm:ss")) ??
        "-",
    },
    {
      name: t("pages.default.IntraSeanceCollateral.table.contract"),
      selector: "contractName",
      cell: (row) => row?.contractName ?? "-",
    },
    {
      name: t("pages.default.IntraSeanceCollateral.table.transactionDirection"),
      selector: "transactionDirection",
      cell: (row) => row?.transactionDirection ?? "-",
    },
    {
      name: t("pages.default.IntraSeanceCollateral.table.transactionType"),
      selector: "transactionType",
      cell: (row) => transactionTypeTranslation[row?.transactionType] ?? "-",
    },
    {
      name: t("pages.default.IntraSeanceCollateral.table.status"),
      selector: "status",
      cell: (row) => row?.status ?? "-",
    },
    {
      name: t("pages.default.IntraSeanceCollateral.table.amount"),
      selector: "currentBlockedAmount",
      cell: (row) => convertAmountToFrontend(row?.currentBlockedAmount) ?? "-",
    },
    {
      name: t("pages.default.IntraSeanceCollateral.table.calculatedCollateral"),
      selector: "calculatedCollateral",
      cell: (row) =>
        convertNumberToFrontend({ num: row?.calculatedCollateral, digit: 2 }) ??
        "-",
    },
  ];
  const sessionCollateralInfoColumns = [
    {
      name: t("pages.default.IntraSeanceCollateral.table.currentCollateral"),
      selector: "depositPrice",
      cell: (row) =>
        convertNumberToFrontend({ num: row.depositPrice, digit: 2 }),
    },
    {
      name: t("pages.default.IntraSeanceCollateral.table.currentBlockedAmount"),
      selector: "currentBlockedPrice",
      cell: (row) =>
        convertNumberToFrontend({ num: row.currentBlockedPrice, digit: 2 }),
    },
    {
      name: t("pages.default.IntraSeanceCollateral.table.transactionLimit"),
      selector: "currentCollateralPrice",
      cell: (row) =>
        convertNumberToFrontend({ num: row.currentCollateralPrice, digit: 2 }),
    },
  ];
  useEffect(() => {
    dispatch(getAllSessionsRequested());
    filterSessionCollateralStatus();
  }, []);

  useEffect(() => {
    if (sessionDate) {
      setSessionDate(
        sessionDate ?? allSessionList?.[0]?.name.split("-").reverse().join("-")
      );
    }
  }, [sessionDate]);

  const handleClick = () => {
    dispatch(
      getSessionCollateralRequest({
        organizationId: organizationId ?? null,
        sessionDate: sessionDate ?? null,
      })
    );
  };

  const filterSessionCollateral = (e) => {
    let { value } = e.target;
    let sessionTime = allSessionList
      ?.find((session) => session.value === parseInt(value))
      ?.name.split("-")
      .reverse()
      .join("-");
    setSessionDate(sessionTime);
  };

  const filterSessionCollateralStatus = () => {
    let params = {
      sessionDate:
        sessionDate ?? allSessionList?.[0]?.name.split("-").reverse().join("-"),
      organizationId: organizationId ?? null,
    };
    if (params?.organizationId && params?.sessionDate) {
      dispatch(getSessionCollateralRequest({ ...params }));
      dispatch(getSessionCurrentBlockedAmountDetailsRequest({ ...params }));
      setSessionDate(sessionDate);
    }
  };

  return (
    <>
      <h5 className="km__u--text-has-emphasis">
        {t("pages.default.IntraSeanceCollateral.title")}
      </h5>
      <div className="km__p--card">
        <div className="km__p--market-results-contracts-header">
          <div className="km__p--market-results-contracts-header-filter">
            <p className="km__p--market-results-contracts-header-filter-name">
              {`${t(
                "pages.default.IntraSeanceCollateral.organizationName"
              )} : ${organizationName}`}
            </p>
            {allSessionList && (
              <Select
                options={allSessionList}
                name="sessionDate"
                defaultValue={allSessionList[0]}
                onChange={(e) => filterSessionCollateral(e)}
              />
            )}

            <Button
              label={t("ui.default.filter")}
              onClick={filterSessionCollateralStatus}
            />
          </div>
        </div>
      </div>
      <div className="km__p--card">
        <div className="km__p--market-results-contracts-header">
          <p className="km__u--text_title">
            {t("pages.default.IntraSeanceCollateral.infoCollateral")}
          </p>
        </div>
        <DataTableServerSideRendering
          columns={sessionCollateralInfoColumns}
          data={inSessionCollateralInformation?.item}
          rowKey={"id"}
        />
      </div>
      <p style={{ marginTop: "20px" }}>
        {t(
          "pages.default.IntraSeanceCollateral.currentCollateralAmountUpdateDate"
        )}{" "}
        {Array.isArray(inSessionCollateralInformation?.item) &&
          moment(
            inSessionCollateralInformation?.item?.[0]
              ?.currentCollateralAmountUpdateDate
          ).isValid() &&
          moment(
            inSessionCollateralInformation?.item?.[0]
              ?.currentCollateralAmountUpdateDate
          ).format("DD/MM/YYYY HH:mm")}
      </p>
      <p style={{ marginTop: "20px" }}>
        {t("pages.default.IntraSeanceCollateral.collateralCalculateDate")}{" "}
        {Array.isArray(inSessionCollateralInformation?.item) &&
        Boolean(
          inSessionCollateralInformation?.item?.[0]?.collateralCalculateDate
        )
          ? moment(
              inSessionCollateralInformation?.item?.[0]?.collateralCalculateDate
            ).isValid() &&
            moment(
              inSessionCollateralInformation?.item?.[0]?.collateralCalculateDate
            ).format("DD/MM/YYYY HH:mm")
          : "-"}
      </p>
      <div className="km__p--card">
        <div className="km__p--market-results-contracts-header">
          <p className="km__u--text_title">
            {t("pages.default.IntraSeanceCollateral.table.title")}
          </p>
        </div>
        <DataTableServerSideRendering
          columns={sessionCollateralStatusColumns}
          data={currentBlockedPriceDetailList}
          rowKey="id"
          handleFetchData={handleClick}
        />
      </div>
    </>
  );
}
